import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Clock, AlertTriangle, ArrowRight, ShieldCheck } from "lucide-react";
import { api, imageUrl } from "../lib/api";
import { BikeDetailModal } from "./BikeDetailModal";

const BikeCard = ({ product, onOpen, index }) => (
  <motion.article
    data-testid={`product-card-${product.id}`}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
    className="group relative flex flex-col bg-[#FAFAFC] text-forest-deep shadow-xl"
  >
    <button
      data-testid={`product-image-${product.id}`}
      onClick={() => onOpen(product)}
      className="relative block overflow-hidden text-left"
    >
      <img
        src={imageUrl(product.image)}
        alt={product.name}
        loading="lazy"
        className="aspect-[4/3] w-full object-contain p-4"
      />
      <span className="absolute left-3 top-3 bg-forest-deep px-2.5 py-1 font-mono text-[9px] font-bold tracking-[0.18em] text-volt uppercase">
        {product.badge}
      </span>
      <span className="absolute right-3 top-3 flex items-center gap-1.5 bg-cyanx px-2.5 py-1 font-mono text-[9px] font-bold tracking-[0.12em] text-forest-deep uppercase">
        <Clock className="h-3 w-3" strokeWidth={2.5} />
        {product.waiting_time}
      </span>
    </button>

    <div className="flex flex-1 flex-col p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-serif text-2xl font-black uppercase leading-none">{product.name}</h3>
        </div>
        {product.ages && (
          <span className="shrink-0 border-2 border-forest-deep px-2 py-1 font-mono text-[9px] font-bold tracking-[0.1em] uppercase">
            Ages {product.ages}
          </span>
        )}
      </div>

      <div className="mt-4 grid grid-cols-3 gap-px bg-slate-200">
        {[product.stat1, product.stat2, product.stat3].map(([k, v]) => (
          <div key={k} className="bg-white px-2.5 py-2">
            <p className="font-mono text-[8px] tracking-[0.12em] text-slate-400 uppercase">{k}</p>
            <p className="mt-0.5 font-mono text-[11px] font-bold text-forest-deep">{v}</p>
          </div>
        ))}
      </div>

      <p className="mt-4 font-serif text-lg font-bold uppercase text-brass">{product.price_note}</p>

      <div data-testid={`legal-disclaimer-${product.id}`} className="disclaimer-box mt-3 flex items-start gap-2 px-3 py-2">
        {product.audience === "road" ? (
          <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-cyanx" />
        ) : (
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brass" />
        )}
        <p className="font-mono text-[8.5px] font-medium leading-relaxed tracking-[0.04em] uppercase text-forest-deep">
          {product.disclaimer}
        </p>
      </div>

      <div className="mt-4 flex gap-2 pt-1">
        <button
          data-testid={`product-details-button-${product.id}`}
          onClick={() => onOpen(product)}
          className="flex flex-1 items-center justify-center gap-2 border-2 border-forest-deep px-4 py-3 font-mono text-[10px] font-bold tracking-[0.15em] uppercase transition-colors hover:bg-forest-deep hover:text-bone"
        >
          Full Specs
        </button>
        <button
          data-testid={`enquire-now-button-${product.id}`}
          onClick={() => onOpen(product)}
          className="clip-corner-sm flex flex-1 items-center justify-center gap-2 bg-brass px-4 py-3 font-mono text-[10px] font-bold tracking-[0.15em] text-white uppercase transition-colors hover:bg-brass-bright"
        >
          Enquire
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  </motion.article>
);

export const BikeCatalogue = () => {
  const [products, setProducts] = useState([]);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const adult = products.filter((p) => p.audience === "adult");
  const road = products.filter((p) => p.audience === "road");
  const kids = products.filter((p) => p.audience === "kids");

  return (
    <section data-testid="catalogue-section" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        {/* SECTION 1 — OFF-ROAD & PRIVATE LAND ONLY */}
        <motion.div
          id="off-road"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="scroll-mt-24"
        >
          <p className="mono-label flex items-center gap-3">
            <span className="inline-block h-0.5 w-10 bg-brass" />
            Section 1
          </p>
          <h2 className="mt-3 font-serif text-4xl font-black uppercase tracking-tight text-bone sm:text-5xl">
            Off-road &amp; <span className="text-brass">private land only</span>
          </h2>
          <div
            data-testid="offroad-section-legal-notice"
            className="disclaimer-box mt-5 flex max-w-2xl items-start gap-3 px-4 py-3"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-brass" />
            <p className="font-mono text-[10px] font-medium leading-relaxed tracking-[0.06em] text-bone/90 uppercase">
              These bikes are NOT road legal. For use on private land only, with
              the landowner's permission. Rider assumes full responsibility for
              safe and lawful use.
            </p>
          </div>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-sand">
            Sur-Ron, Talaria and OSET. Pre-order with clear lead times. Every
            price we quote is one all-in number — UK delivery included.
          </p>
        </motion.div>

        <p className="mono-label mt-12 flex items-center gap-3">
          <span className="inline-block h-0.5 w-6 bg-brass" />
          Adult — Sur-Ron &amp; Talaria
        </p>
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          {adult.map((p, i) => (
            <BikeCard key={p.id} product={p} index={i} onOpen={setDetail} />
          ))}
        </div>

        <p className="mono-label mt-16 flex items-center gap-3">
          <span className="inline-block h-0.5 w-6 bg-volt" />
          Kids — OSET Electric Trials (ages 4–14)
        </p>
        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {kids.map((p, i) => (
            <BikeCard key={p.id} product={p} index={i} onOpen={setDetail} />
          ))}
        </div>

        {/* SECTION 2 — ROAD LEGAL */}
        <motion.div
          id="road-legal"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-24 scroll-mt-24"
        >
          <p className="mono-label flex items-center gap-3">
            <span className="inline-block h-0.5 w-10 bg-cyanx" />
            Section 2
          </p>
          <h2 className="mt-3 font-serif text-4xl font-black uppercase tracking-tight text-bone sm:text-5xl">
            Road legal <span className="text-cyanx">in the UK</span>
          </h2>
          <p className="mt-3 max-w-xl text-base leading-relaxed text-sand">
            These bikes are EN 15194 certified and fully legal to ride on UK
            public roads. No licence required. One all-in price — delivery
            included, no hidden costs.
          </p>
          <div
            data-testid="road-legal-mod-warning"
            className="mt-5 flex max-w-2xl items-start gap-3 border border-cyanx/30 bg-cyanx/5 px-4 py-3"
          >
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-cyanx" />
            <p className="font-mono text-[10px] font-medium leading-relaxed tracking-[0.06em] text-bone/90 uppercase">
              Modifying an e-bike — for example removing the speed limiter — can
              make it illegal on UK roads and voids its certification.
            </p>
          </div>
        </motion.div>

        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {road.map((p, i) => (
            <BikeCard key={p.id} product={p} index={i} onOpen={setDetail} />
          ))}
        </div>
      </div>

      <BikeDetailModal product={detail} onClose={() => setDetail(null)} />
    </section>
  );
};
